import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import {Button, Icon} from '@albert-io/atomic';
import QuestionTrackerModal from 'client/components/PracticeView/QuestionTrackerModal/QuestionTrackerModal.react';
import TrackedButton from 'client/components/Tracking/TrackedButton.react';
import sessionStore from 'client/Session/SessionStore';
import {getModelForResourceType} from 'resources/modelRegistry';
import {getQuestionEditPath} from 'client/Dennis/Content/Queue/shared';
import {getUserPointsQuery} from 'client/GlobalNavigation/PointsIndicator/PointsIndicator.react';

/**
 * @todo: Question stores should be rethought, IMO. Let's clean this up later on.
 * One idea is to have question option selection be state on the question renderer.
 */
import {callTargetedAction} from 'client/framework';
import questionTypeActions from 'components/QuestionTypes/questionType.actions';
import {showGlobalLoginModal} from 'client/LogIn/utils';

import UpgradeSubjectModal from '../UpgradeSubjectModal/UpgradeSubjectModal.react';

const questionProp = PropTypes.instanceOf(getModelForResourceType('question_v3'));
const questionSetProp = PropTypes.instanceOf(getModelForResourceType('question_set_v1'));

/**
 * @todo: Question stores should be rethought, IMO. Let's clean this up later on.
 * One idea is to have question option selection be state on the question renderer.
 */

export function loadGuess(questionStoreName, guess) {
  callTargetedAction({
    name: questionTypeActions.LOAD_GUESS,
    targetStore: questionStoreName,
    payload: guess
  });
}

export function clearGuess(questionStoreName) {
  callTargetedAction({
    name: questionTypeActions.CLEAR_GUESS,
    targetStore: questionStoreName
  });
}

function updateQuestionAtStore(questionStoreName, question) {
  callTargetedAction({
    name: questionTypeActions.UPDATE_QUESTION,
    targetStore: questionStoreName,
    payload: question
  });
}

const TryAgain = ({handleResetGuess, questionStoreName}) => (
  <Button
    type='button'
    onClick={() => {
      global.scrollTo(0, 0);
      handleResetGuess();
      clearGuess(questionStoreName);
    }}
  >
    Try again
  </Button>
);
TryAgain.propTypes = {
  handleResetGuess: PropTypes.func.isRequired,
  questionStoreName: PropTypes.string
};

const ToggleViewSolution = ({
  question,
  isViewingSolution,
  handleToggleSolution,
  questionStoreName
}) => {
  const builtGuess = question.getStore().buildCorrectGuess(question);
  return (
    <Button
      type='button'
      data-testid='practice-view__toggle-solution-btn'
      onClick={() => {
        if (isViewingSolution) {
          clearGuess(questionStoreName);
        } else {
          loadGuess(questionStoreName, builtGuess);
        }
        handleToggleSolution(builtGuess);
      }}
    >
      {isViewingSolution ? 'Hide' : 'Show'} solution
    </Button>
  );
};
ToggleViewSolution.propTypes = {
  question: questionProp,
  isViewingSolution: PropTypes.bool,
  handleToggleSolution: PropTypes.func,
  questionStoreName: PropTypes.string
};

const EditQuestionLink = ({question, questionSet}) => (
  <Button
    color='secondary'
    as={Link}
    to={getQuestionEditPath({
      questionSetId: questionSet.getId(),
      questionId: question.getId(),
      customSearch: {
        status: 'published',
        subject: !questionSet.getSubject().isEmpty()
          ? questionSet.getSubject().getId()
          : questionSet.getAuthoringSubject().getId(),
        page: questionSet.getId(),
        fallback_page: 1
      }
    })}
  >
    Edit
  </Button>
);

EditQuestionLink.propTypes = {
  question: questionProp,
  questionSet: questionSetProp
};

const Next = ({onClick, disabled}) => (
  <Button type='button' onClick={onClick} disabled={disabled} variant='outlined'>
    Skip For Now
  </Button>
);

Next.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

class MoreInfo extends React.Component {
  static propTypes = {
    questionSetId: PropTypes.string,
    userId: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      showModal: false
    };
  }

  toggleModal = () => {
    this.setState((state) => ({showModal: !state.showModal}));
  };

  render() {
    const {questionSetId, userId} = this.props;
    return (
      <div>
        <TrackedButton type='button' onClick={this.toggleModal}>
          My usage
        </TrackedButton>
        {this.state.showModal && (
          <QuestionTrackerModal
            handleClose={this.toggleModal}
            questionSetId={questionSetId}
            userId={userId}
          />
        )}
      </div>
    );
  }
}

class SubmitAnswer extends React.Component {
  static propTypes = {
    assignmentId: PropTypes.string,
    onSuccess: PropTypes.func,
    /**
     * Proptypes below are provided automatically through element cloning in the parent
     */
    question: questionProp,
    questionSet: PropTypes.instanceOf(getModelForResourceType('question_set_v1')),
    subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
    onError: PropTypes.func,
    onClick: PropTypes.func,
    /* A function that, when called, returns the time spent on a question. */
    getTimeElapsed: PropTypes.func.isRequired,
    hasAccess: PropTypes.bool.isRequired
  };

  static defaultProps = {
    onSuccess: () => {},
    onError: (err) => {
      throw err;
    },
    onClick: () => {}
  };

  constructor(props) {
    super(props);
    this.state = {
      isSubmitPending: false,
      shouldShowUpgradeSubjectModal: false
    };
  }

  toggleShowUpgradeSubjectModal = () => {
    this.setState(({shouldShowUpgradeSubjectModal}) => ({
      shouldShowUpgradeSubjectModal: !shouldShowUpgradeSubjectModal
    }));
  };

  submitGuess = async () => {
    if (this.state.isSubmitPending) {
      return;
    }
    this.setState({isSubmitPending: true});
    const {question, getTimeElapsed, assignmentId, onSuccess, onError} = this.props;
    const questionStore = question.getStore();
    const guess = questionStore.buildGuess({
      timeElapsed: getTimeElapsed()
    });
    try {
      const response = await guess.submitGuess({
        assignmentId,
        userId: sessionStore.getUserId(),
        questionType: question.getQuestionType()
      });
      updateQuestionAtStore(questionStore.getName(), response.getQuestion());
      getUserPointsQuery(sessionStore.getUserId()).invalidateInterest();
      onSuccess(response);
    } catch (err) {
      onError(err);
    } finally {
      this.setState({isSubmitPending: false});
    }
  };

  handleSubmit = (e) => {
    const isLoggedIn = sessionStore.hasValidSession();
    if (!isLoggedIn) {
      showGlobalLoginModal();
      return;
    }
    if (!this.props.hasAccess) {
      this.toggleShowUpgradeSubjectModal();
      return;
    }
    this.submitGuess();
    this.props.onClick(e);
  };

  render() {
    const isMarkAsCorrect = this.props.question.getRubricType() === 'markAsCorrect';
    const isFreeResponse = this.props.question.getQuestionType() === 'free-response';
    const {
      question,
      /* eslint-disable */
      assignmentId,
      /* Plucking off props that shouldn't be passed to <button> */
      getTimeElapsed,
      onSuccess,
      questionStoreName,
      questionSet,
      navigateBackProps,
      navigateNextProps,
      subject,
      /* eslint-enable */
      ...rest
    } = this.props;
    const {isSubmitPending} = this.state;
    let disabled = rest.disabled || isSubmitPending;
    let label = 'Submit answer';
    let submittedLabel = 'Submitted';

    if (isFreeResponse) {
      // in questionmodel, markAsCorrect rubric types have an early return to false
      disabled = disabled || !question.getStore().hasUserSelectedAnswer;
      label = 'Submit response';
    } else if (isMarkAsCorrect) {
      disabled = disabled || !question.hasSelectedAnswer();
      label = 'Mark as completed';
      submittedLabel = 'Completed';
    } else {
      disabled = disabled || !question.hasSelectedAnswer();
      label = 'Submit answer';
    }

    return (
      <Button
        {...rest}
        disabled={disabled || this.state.isSubmitPending}
        onClick={this.handleSubmit}
        type='submit'
        data-testid='practice-view__submit-answer-btn'
        variant={rest.guess ? 'text' : 'primary'}
        className={rest.guess ? 'u-color_green-700 u-opacity_full' : ''}
      >
        {rest.guess ? <Icon icon='check-circle' iconStyle='regular' className='u-mar-r_1' /> : null}
        {rest.guess ? submittedLabel : label}
        {this.state.shouldShowUpgradeSubjectModal && (
          <UpgradeSubjectModal subject={subject} handleClose={this.toggleShowUpgradeSubjectModal} />
        )}
      </Button>
    );
  }
}

const QuestionActions = ({children, ...rest}) => {
  return (
    <div className='question-actions-wrapper'>
      {React.Children.map(children, (child) => React.cloneElement(child, rest))}
    </div>
  );
};

const primitives = {
  SubmitAnswer,
  TryAgain,
  ToggleViewSolution,
  MoreInfo,
  EditQuestionLink,
  Next
};

Object.entries(primitives).forEach(([name, component]) => {
  QuestionActions[name] = component;
});

QuestionActions.propTypes = {
  children: (props, propName, componentName) => {
    const allowedChildTypes = Object.values(primitives);

    for (const child of React.Children.toArray(props.children)) {
      if (allowedChildTypes.includes(child.type) === false) {
        return new Error(
          `Invalid child passed to ${componentName}. Please ensure all children are allowed ${componentName} primitives.`
        );
      }
    }
    return null;
  }
};

export default QuestionActions;
